<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute } from "vue-router";
import Icon from "@/design/primitives/Icon.vue";
import { siteBuilding, useOnboardingState } from "@/features/onboarding/api/use-onboarding";
import { JOURNEY_DONE, nextStep } from "./home-steps";
import { homeMessages } from "./i18n/home";
import { useHomeSteps } from "./use-home-steps";

const route = useRoute();
const { steps } = useHomeSteps();
const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: homeMessages });
const index = computed(() =>
	steps.value.findIndex(
		(step) => route.path === step.to || (step.to === "/inbox" && route.path.startsWith("/inbox/")),
	),
);
const current = computed(() => steps.value[index.value]);
// O mesmo "próximo passo" da home: o primeiro por fazer, não o vizinho da lista.
const next = computed(() => nextStep(steps.value));
const previous = computed(() => steps.value[index.value - 1]);
const done = computed(() => steps.value.filter((step) => step.done).length);
const complete = computed(() => steps.value.every((step) => step.done));
/**
 * O SITE QUE SE CONSTRÓI ENQUANTO A PESSOA SEGUE (23/10): a faixa diz em toda etapa
 * que ele corre, e quando fica pronto, com o caminho para o ver.
 */
const { data: onboarding } = useOnboardingState();
const building = computed(() => siteBuilding(onboarding.value));
const siteReady = computed(() => onboarding.value?.siteBuild?.status === "succeeded");
const destination = (path: string) =>
	path.startsWith("/comecar") ? path : { path, query: { onboarding: "1" } };
</script>

<template>
 <section v-if="current" class="shrink-0 border-b border-border bg-surface px-4 py-2 sm:px-6 sm:py-3" :aria-label="t('title')">
  <div class="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
   <!-- No telefone, UMA linha: "Etapa 1 de 5 · O título", cortada com reticências;
        o progresso e a descrição ficam para telas largas (04/10). -->
   <div aria-live="polite" class="min-w-0 max-sm:flex max-sm:w-full max-sm:items-baseline max-sm:gap-1" data-testid="journey-banner">
    <p class="text-xs text-fg-muted max-sm:shrink-0">{{ t('steps.journeyStep', { step: index + 1, total: steps.length }) }}<span class="max-sm:hidden"> · {{ t('steps.progress', { done, total: steps.length }) }}</span><span class="sm:hidden" aria-hidden="true"> ·</span></p>
    <p class="text-sm font-semibold max-sm:min-w-0 max-sm:truncate">{{ complete ? t('steps.allDoneTitle') : t(current.titleKey) }}</p>
    <p v-if="!current.done" class="text-xs text-fg-muted max-sm:hidden">{{ t(current.bodyKey) }}</p>
   </div>
   <p v-if="building" role="status" class="inline-flex items-center gap-1.5 rounded-full bg-accent-subtle px-3 py-1 text-xs font-medium text-accent">
    <Icon name="sparkle" class="size-3.5 animate-pulse" />{{ t('steps.siteBuilding') }}
   </p>
   <RouterLink v-else-if="siteReady" :to="{ path: '/site', query: { onboarding: '1' } }" class="inline-flex items-center gap-1.5 rounded-full bg-success-subtle px-3 py-1 text-xs font-medium text-fg">
    <Icon name="check" class="size-3.5" />{{ t('steps.siteReady') }}
   </RouterLink>
   <div class="flex items-center gap-4">
    <!-- No telefone a faixa é curta (23/10): voltar fica para o navegador, e o botão diz só "Próximo passo". -->
    <RouterLink v-if="previous" :to="destination(previous.to)" class="text-sm text-accent underline max-sm:hidden">{{ t('steps.journeyBack') }}</RouterLink>
    <RouterLink v-if="complete" :to="JOURNEY_DONE" class="rounded-full bg-accent px-4 py-2 text-sm font-medium text-fg-on-accent">{{ t('steps.journeyFinish') }}</RouterLink>
    <RouterLink v-else-if="current.done && next" :to="destination(next.to)" class="inline-flex items-center gap-1 rounded-full bg-accent px-4 py-2 text-sm font-medium text-fg-on-accent"><span>{{ t('steps.now') }}<span class="max-sm:hidden">: {{ t(next.titleKey) }}</span></span> <Icon name="arrowRight" class="size-4" /></RouterLink>
   </div>
  </div>
 </section>
</template>
